import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Eye } from 'lucide-react'
import { toast } from 'sonner'
import { revealSecretValue } from '../../hooks/use-secrets'
import { cn } from '../../lib/utils'
import type { RevealedSecretValue, SecretEntry } from './secret-dashboard-types'

interface SecretRevealPanelProps {
  entry: SecretEntry
  isDark: boolean
}

function inputClass(isDark: boolean) {
  return cn(
    'w-full rounded-md border px-3 py-2 text-base outline-none md:text-sm',
    isDark ? 'border-white/[0.08] bg-surface-2 text-neutral-100 placeholder-neutral-600' : 'border-neutral-200 bg-white text-neutral-900 placeholder-neutral-400',
  )
}

export function SecretRevealPanel({ entry, isDark }: SecretRevealPanelProps) {
  const [reason, setReason] = useState('')
  const [revealed, setRevealed] = useState<RevealedSecretValue | null>(null)
  const [pending, setPending] = useState(false)
  const qc = useQueryClient()

  useEffect(() => {
    setReason('')
    setRevealed(null)
  }, [entry.id])

  async function handleReveal() {
    if (!reason.trim()) {
      toast.error('Reason required to reveal')
      return
    }
    setPending(true)
    try {
      setRevealed(await revealSecretValue(entry.id, reason.trim()))
      qc.invalidateQueries({ queryKey: ['secret-audit'] })
    } catch (err) {
      toast.error('Reveal failed', { description: err instanceof Error ? err.message : undefined })
    } finally {
      setPending(false)
    }
  }

  return (
    <section className="space-y-2">
      <div className="text-sm font-medium">Reveal</div>
      <input className={inputClass(isDark)} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason required to reveal" />
      <button type="button" onClick={handleReveal} disabled={pending} className="inline-flex items-center gap-2 rounded-md bg-neutral-900 px-3 py-2 text-sm text-white disabled:opacity-50">
        <Eye className="h-4 w-4" /> {pending ? 'Revealing...' : 'Reveal'}
      </button>
      {revealed ? (
        <div className={cn('rounded-md p-3 font-mono text-xs', isDark ? 'bg-surface-2' : 'bg-neutral-100')}>
          <div className="break-all">{revealed.key}={revealed.value}</div>
          <button type="button" className="mt-2 text-brand-600" onClick={() => setRevealed(null)}>Clear</button>
        </div>
      ) : null}
    </section>
  )
}
